import React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '../../utils/cn'

export const TooltipProvider = TooltipPrimitive.Provider
export const TooltipRoot = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-[1500] max-w-xs rounded-md bg-content-primary px-3 py-2 text-xs text-content-inverse shadow-md',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = 'TooltipContent'

type Placement =
  'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'

function parsePlacement(placement: Placement = 'top'): {
  side: 'top' | 'bottom' | 'left' | 'right'
  align: 'start' | 'center' | 'end'
} {
  const [side, align] = placement.split('-') as ['top' | 'bottom' | 'left' | 'right', string?]
  return { side, align: align === 'start' ? 'start' : align === 'end' ? 'end' : 'center' }
}

export interface TooltipProps {
  label?: React.ReactNode
  children: React.ReactNode
  placement?: Placement
  openDelay?: number
  closeDelay?: number
  isDisabled?: boolean
  hasArrow?: boolean
  className?: string
}

/**
 * Tooltip wrapper on Radix. Renders `label` on hover/focus of
 * `children`. When `label` is empty or `isDisabled`, children render unwrapped.
 */
export function Tooltip({
  label,
  children,
  placement = 'top',
  openDelay,
  isDisabled,
  hasArrow,
  className,
}: TooltipProps) {
  if (!label || isDisabled) return <>{children}</>
  const { side, align } = parsePlacement(placement)
  const trigger =
    typeof children === 'string' || typeof children === 'number' ? (
      <span>{children}</span>
    ) : (
      children
    )

  return (
    <TooltipPrimitive.Root delayDuration={openDelay}>
      <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>
      <TooltipContent side={side} align={align} className={className}>
        {label}
        {hasArrow && <TooltipPrimitive.Arrow className="fill-content-primary" />}
      </TooltipContent>
    </TooltipPrimitive.Root>
  )
}
